import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getVisitorPurposes } from "../../api/visitorPurposeApi";
import { getVisitorIdProofTypes } from "../../api/visitorIdProofApi";
import { filterActiveMasterRows } from "../../api/apiHelpers";

const VisitorContext = createContext(null);

export function VisitorProvider({ children }) {
  const [purposes, setPurposes] = useState([]);
  const [idProofTypes, setIdProofTypes] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const loadLookups = useCallback(async () => {
    setLoadingLookups(true);
    setLookupError("");
    try {
      const [purposeResp, idProofResp] = await Promise.all([
        getVisitorPurposes(),
        getVisitorIdProofTypes(),
      ]);
      setPurposes(filterActiveMasterRows(purposeResp?.data ?? []));
      setIdProofTypes(filterActiveMasterRows(idProofResp?.data ?? []));
    } catch (err) {
      setLookupError(err?.message || "Failed to load visitor lookups");
      setPurposes([]);
      setIdProofTypes([]);
    } finally {
      setLoadingLookups(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      purposes,
      idProofTypes,
      loadingLookups,
      lookupError,
      loadLookups,
    }),
    [purposes, idProofTypes, loadingLookups, lookupError, loadLookups],
  );

  return (
    <VisitorContext.Provider value={value}>{children}</VisitorContext.Provider>
  );
}

export function useVisitorContext() {
  const ctx = useContext(VisitorContext);
  if (!ctx) {
    throw new Error("useVisitorContext must be used within VisitorProvider");
  }
  return ctx;
}
